/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { 
  DynamicEvent, 
  ActiveEvent, 
  EventEngineState, 
  TriggerCondition,
  MarketModifier,
  EventOutcome,
  EventDefinition
} from '@/types/dynamicEvents';
import type { TycoonGameState } from '@/types/tycoon';
import type { PhaseProgressionPreferences } from '@smartphone-tycoon/shared-core';
import { DEVELOPMENT_CONSTANTS } from '@/config/gameConstants';
import { 
  shouldShowPhasePrompt, 
  getPhaseChoice,
  getPreferenceInfo
} from '../simplePreferenceService';

export class EventEngine {
  private state: EventEngineState;
  private eventDefinitions: DynamicEvent[] = [];
  private listeners: Set<(event: ActiveEvent) => void> = new Set();
  private phasePreferences: PhaseProgressionPreferences | null = null;
  private phasePreferencesLastChanged: Date | null = null;
  private productProgressionCallback: ((productId: string, targetPhase: string, gameDate: { day: number; month: number; year: number }, investment?: any) => void) | null = null;

  constructor() {
    this.state = {
      activeEvents: [],
      eventHistory: [],
      activeModifiers: new Map(),
      lastProcessedMonth: -1, // Start at -1 so month 0 can be processed
      eventCooldowns: new Map()
    };
  }

  // Event Management
  loadEventDefinitions(definitions: EventDefinition): void {
    this.eventDefinitions = definitions.events;
  }

  addListener(callback: (event: ActiveEvent) => void): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // Phase preferences management
  setPhasePreferences(preferences: PhaseProgressionPreferences, lastChanged?: Date): void {
    this.phasePreferences = preferences;
    if (lastChanged) {
      this.phasePreferencesLastChanged = lastChanged;
    }
  }

  getPhasePreferences(): PhaseProgressionPreferences | null {
    return this.phasePreferences;
  }

  getPhasePreferencesLastChanged(): Date | null {
    return this.phasePreferencesLastChanged;
  }

  // Auto-handle callback is no longer needed - events are handled internally
  // Kept for backward compatibility but not used
  setAutoHandleCallback(callback: (eventId: string, choiceId: string) => void): void {
    // No-op - auto-handling is now done internally
  }

  // Set callback for product progression when auto-handling events
  setProductProgressionCallback(callback: (productId: string, targetPhase: string, gameDate: { day: number; month: number; year: number }, investment?: any) => void): void {
    this.productProgressionCallback = callback;
  }

  // Simple auto-handle method for phase events
  private autoHandlePhaseEventSimple(
    event: DynamicEvent, 
    productId: string, 
    productName: string, 
    phase: string, 
    preference: 'proceed' | 'minor' | 'major', 
    gameState: TycoonGameState
  ): { success: boolean; choiceId?: string; error?: string } {
    // Map preference to standardized choice IDs
    let choiceId = '';
    if (preference === 'proceed') {
      choiceId = 'proceed_to_next_stage';
    } else if (preference === 'minor') {
      choiceId = 'minor_investment';
    } else if (preference === 'major') {
      choiceId = 'major_investment';
    }

    const choice = event.choices?.find(c => c.id === choiceId);
    if (!choice) {
      console.warn(`Auto-handle failed: choice ${choiceId} not found for event ${event.id}`);
      return { success: false, error: `Choice ${choiceId} not found` };
    }

    // Check cost requirements
    if (choice.costRequirement?.cash && gameState.cash < choice.costRequirement.cash) {
      console.warn(`Auto-handle failed: insufficient funds for choice ${choiceId}`);
      return { success: false, error: 'Insufficient funds' };
    }

    // Create the active event and resolve it directly without UI interaction
    const activeEvent = this.activateEvent(event, this.state.lastProcessedMonth + 1, gameState);
    
    // Add product context for phase events
    activeEvent.productContext = { productId, productName, phase };
    
    // Replace placeholders in event text
    if (activeEvent.event.title) {
      activeEvent.event.title = activeEvent.event.title.replace('{productName}', productName);
    }
    if (activeEvent.event.description) {
      activeEvent.event.description = activeEvent.event.description.replace('{productName}', productName);
    }
    
    // Set the player choice and resolve immediately
    activeEvent.playerChoice = choiceId;
    this.resolveEvent(activeEvent, choice.outcome);
    
    // Apply product progression effects if callback is set
    if (this.productProgressionCallback && choice.outcome.immediateEffects) {
      const effects = choice.outcome.immediateEffects;
      
      // Handle investment choices
      if (preference === 'minor' || preference === 'major') {
        // Apply investment effects without complex state tracking
        const investment: any = {};
        if (effects.developmentPhase) {
          investment.additionalDays = effects.developmentPhase.additionalDays;
          investment.confidence = effects.developmentPhase.confidence;
          investment.qualityScore = effects.developmentPhase.qualityScore;
        }
        if (effects.techScore) {
          investment.techScore = effects.techScore;
        }
        if (effects.marketAppeal) {
          investment.marketAppeal = effects.marketAppeal;
        }
        if (effects.productionCostReduction) {
          investment.productionCostReduction = effects.productionCostReduction;
        }

        if (Object.keys(investment).length > 0) {
          this.productProgressionCallback(productId, 'investment', gameState, investment);
        }
        
        console.log(`Applied investment (${preference}) for ${productName} in ${phase} phase`);
      } else {
        // Non-investment choice - apply phase progression immediately
        if (effects.phaseProgression?.targetPhase) {
          console.log(`Auto-progressing ${productName} from ${phase} to ${effects.phaseProgression.targetPhase}`);
          this.productProgressionCallback(
            productId, 
            effects.phaseProgression.targetPhase, 
            { day: gameState.day, month: gameState.month, year: gameState.year }
          );
        } else {
          console.warn(`No target phase specified for progression of ${productName} from ${phase}`);
        }
      }
    }
    
    return { success: true, choiceId };
  }


  // Main processing function called from game simulation
  processEvents(gameState: TycoonGameState, currentMonth: number): ActiveEvent[] {
    const newEvents: ActiveEvent[] = [];

    // Clean up expired events and modifiers
    this.cleanupExpiredEvents(currentMonth);
    this.cleanupExpiredModifiers();

    // Only process if we've advanced to a new month
    if (currentMonth > this.state.lastProcessedMonth) {
      // Evaluate triggers for new events
      const triggeredEvents = this.evaluateTriggers(gameState, currentMonth);
      
      if (triggeredEvents.length > 0) {
        console.log('Dynamic events triggered:', triggeredEvents.map(e => e.title));
      }
      
      for (const event of triggeredEvents) {
        const activeEvent = this.activateEvent(event, currentMonth, gameState);
        newEvents.push(activeEvent);
        
        // Notify listeners
        this.listeners.forEach(listener => listener(activeEvent));
      }

      this.state.lastProcessedMonth = currentMonth;
    }

    return newEvents;
  }

  // Trigger evaluation
  private evaluateTriggers(gameState: TycoonGameState, currentMonth: number): DynamicEvent[] {
    const triggeredEvents: DynamicEvent[] = [];

    for (const eventDef of this.eventDefinitions) {
      // Check cooldowns
      const cooldownExpiry = this.state.eventCooldowns.get(eventDef.id);
      if (cooldownExpiry && currentMonth < cooldownExpiry) {
        continue;
      }

      // Check prerequisites
      if (eventDef.prerequisiteEvents) {
        const hasPrerequisites = eventDef.prerequisiteEvents.every(prereqId =>
          this.state.eventHistory.some(ae => ae.event.id === prereqId && ae.status === 'resolved')
        );
        if (!hasPrerequisites) {
          continue;
        }
      }

      // Evaluate trigger conditions
      const conditionsMet = this.evaluateConditions(eventDef.triggers, gameState, currentMonth);
      
      if (conditionsMet) {
        triggeredEvents.push(eventDef);
        
        // Set cooldown
        if (eventDef.cooldown) {
          this.state.eventCooldowns.set(eventDef.id, currentMonth + eventDef.cooldown);
        }
      }
    }

    return triggeredEvents;
  }

  private evaluateConditions(conditions: TriggerCondition[], gameState: TycoonGameState, currentMonth: number): boolean {
    return conditions.every(condition => this.evaluateCondition(condition, gameState, currentMonth));
  }

  private evaluateCondition(condition: TriggerCondition, gameState: TycoonGameState, currentMonth: number): boolean {
    switch (condition.type) {
      case 'time':
        return this.evaluateTimeCondition(condition, currentMonth);
      case 'stat':
        return this.evaluateStatCondition(condition, gameState);
      case 'milestone':
        return this.evaluateMilestoneCondition(condition, gameState);
      case 'random':
        return Math.random() < (condition.probability || 0.1);
      case 'product_launch':
        return this.evaluateProductLaunchCondition(condition, gameState);
      case 'development_phase':
        return this.evaluateDevelopmentPhaseCondition(condition, gameState);
      case 'phase_complete':
        return this.evaluatePhaseCompleteCondition(condition, gameState);
      case 'phase_start':
        return this.evaluatePhaseStartCondition(condition, gameState);
      default:
        return false;
    }
  }

  private evaluateTimeCondition(condition: TriggerCondition, currentMonth: number): boolean {
    const targetMonth = Number(condition.value);
    switch (condition.operator) {
      case '>': return currentMonth > targetMonth;
      case '<': return currentMonth < targetMonth;
      case '>=': return currentMonth >= targetMonth;
      case '<=': return currentMonth <= targetMonth;
      case '=': return currentMonth === targetMonth;
      default: return currentMonth >= targetMonth;
    }
  }

  private evaluateStatCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    if (!condition.field || condition.value === undefined) return false;
    
    const statValue = this.getNestedValue(gameState, condition.field);
    const targetValue = Number(condition.value);
    
    switch (condition.operator) {
      case '>': return statValue > targetValue;
      case '<': return statValue < targetValue;
      case '>=': return statValue >= targetValue;
      case '<=': return statValue <= targetValue;
      case '=': return statValue === targetValue;
      default: return statValue >= targetValue;
    }
  }

  private evaluateMilestoneCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    // Implementation depends on specific milestones
    // Example: first product launch, reaching certain market share, etc.
    switch (condition.field) {
      case 'first_product_launched':
        return gameState.products && gameState.products.length > 0;
      case 'market_leader':
        return (gameState.marketShare || 0) > 0.3;
      case 'bankruptcy_risk':
        return (gameState.cash || 0) < 100000;
      default:
        return false;
    }
  }

  private evaluateProductLaunchCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    // Check if there are any products that just launched (status changed to production)
    // This would typically be called when a product transitions to production
    if (!gameState.products) return false;
    
    const productionProducts = gameState.products.filter(p => p.status === 'production');
    if (productionProducts.length === 0) return false;

    // If a specific product ID is specified, check that product
    if (condition.productId) {
      const product = productionProducts.find(p => p.id === condition.productId);
      return !!product;
    }

    // If field and value are specified, evaluate them
    if (condition.field && condition.value !== undefined) {
      // For example: techScore >= 8.0
      for (const product of productionProducts) {
        const productValue = this.getNestedValue(product, condition.field);
        const targetValue = Number(condition.value);
        
        switch (condition.operator) {
          case '>': if (productValue > targetValue) return true; break;
          case '<': if (productValue < targetValue) return true; break;
          case '>=': if (productValue >= targetValue) return true; break;
          case '<=': if (productValue <= targetValue) return true; break;
          case '=': if (productValue === targetValue) return true; break;
          default: if (productValue >= targetValue) return true; break;
        }
      }
      return false;
    }

    // If no specific conditions, just check if any products are in production
    return true;
  }

  private evaluateDevelopmentPhaseCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    if (!gameState.products || !condition.phase) return false;
    
    // Check if any products are in the specified development phase
    const productsInPhase = gameState.products.filter(p => 
      p.status === condition.phase && p.developmentPhase?.phase === condition.phase
    );
    
    return productsInPhase.length > 0;
  }

  private evaluatePhaseCompleteCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    // This will be manually triggered by the product pipeline when a phase completes
    // It's not automatically evaluated during regular event processing
    return false;
  }

  private evaluatePhaseStartCondition(condition: TriggerCondition, gameState: TycoonGameState): boolean {
    // This will be manually triggered by the product pipeline when a phase starts
    // It's not automatically evaluated during regular event processing
    return false;
  }

  private getNestedValue(obj: any, path: string): number {
    return path.split('.').reduce((current, key) => current?.[key], obj) || 0;
  }

  // Event lifecycle
  private activateEvent(event: DynamicEvent, currentMonth: number, gameState?: TycoonGameState): ActiveEvent {
    const gameDate = gameState ? {
      day: gameState.day,
      month: gameState.month,
      year: gameState.year,
      hour: Math.floor(Math.random() * 24), // Random hour for variety
      minute: Math.floor(Math.random() * 60) // Random minute for variety
    } : {
      day: 1,
      month: (currentMonth % 12) + 1,
      year: 2024 + Math.floor(currentMonth / 12),
      hour: 12,
      minute: 0
    };

    const activeEvent: ActiveEvent = {
      event,
      triggeredAt: new Date(),
      gameDate,
      gameMonth: currentMonth,
      status: event.type === 'reactive' ? 'pending' : 'active',
      expiresAt: event.timeLimit ? new Date(Date.now() + event.timeLimit * 1000) : undefined
    };

    this.state.activeEvents.push(activeEvent);

    // Auto-resolve systemic events
    if (event.type === 'systemic' && event.automaticOutcome) {
      this.resolveEvent(activeEvent, event.automaticOutcome);
    }

    return activeEvent;
  }

  resolveEvent(activeEvent: ActiveEvent, outcome: EventOutcome): void {
    activeEvent.outcome = outcome;
    activeEvent.status = 'resolved';

    // Apply market modifiers
    if (outcome.marketModifiers) {
      for (const modifier of outcome.marketModifiers) {
        this.applyMarketModifier(modifier);
      }
    }

    // Move to history
    this.state.eventHistory.push(activeEvent);
    // Use ID comparison instead of object identity (objects may be different references)
    this.state.activeEvents = this.state.activeEvents.filter(ae =>
      ae.event.id !== activeEvent.event.id ||
      ae.productContext?.productId !== activeEvent.productContext?.productId
    );

    // Trigger followup events
    if (outcome.followupEvents) {
      for (const followupId of outcome.followupEvents) {
        const followupEvent = this.eventDefinitions.find(e => e.id === followupId);
        if (followupEvent) {
          // Schedule followup for next processing cycle
          setTimeout(() => {
            const activeFollowup = this.activateEvent(followupEvent, this.state.lastProcessedMonth + 1);
            this.listeners.forEach(listener => listener(activeFollowup));
          }, 100);
        }
      }
    }
  }

  playerChoice(activeEvent: ActiveEvent, choiceId: string): void {
    const choice = activeEvent.event.choices?.find(c => c.id === choiceId);
    if (!choice) return;

    activeEvent.playerChoice = choiceId;
    this.resolveEvent(activeEvent, choice.outcome);
  }

  // Market modifier management
  private applyMarketModifier(modifier: MarketModifier): void {
    const expiresAt = modifier.duration 
      ? new Date(Date.now() + modifier.duration * 30 * 24 * 60 * 60 * 1000) // months to ms
      : undefined;

    this.state.activeModifiers.set(modifier.id, {
      ...modifier,
      appliedAt: new Date(),
      expiresAt
    });
  }

  getActiveModifiers(): Map<string, MarketModifier & { appliedAt: Date; expiresAt?: Date }> {
    return new Map(this.state.activeModifiers);
  }

  // Cleanup
  private cleanupExpiredEvents(currentMonth: number): void {
    const now = new Date();
    
    this.state.activeEvents = this.state.activeEvents.filter(event => {
      if (event.expiresAt && now > event.expiresAt) {
        event.status = 'expired';
        this.state.eventHistory.push(event);
        return false;
      }
      return true;
    });
  }

  private cleanupExpiredModifiers(): void {
    const now = new Date();
    
    for (const [id, modifier] of this.state.activeModifiers) {
      if (modifier.expiresAt && now > modifier.expiresAt) {
        this.state.activeModifiers.delete(id);
      }
    }
  }

  // State management
  getState(): EventEngineState {
    return { ...this.state };
  }

  setState(state: Partial<EventEngineState>): void {
    this.state = { ...this.state, ...state };
  }

  resetState(): void {
    this.state = {
      activeEvents: [],
      eventHistory: [],
      activeModifiers: new Map(),
      lastProcessedMonth: -1,
      eventCooldowns: new Map()
    };
  }

  // Get product preference info for UI display using simple preference system
  getProductPreferenceInfo(productId: string, product?: any): { preference: string; label: string; color: string } | null {
    if (!product || !this.phasePreferences) {
      return null;
    }

    // Determine current phase
    const currentPhase = product.status as 'design' | 'engineering' | 'testing' | 'manufacturing';
    if (!['design', 'engineering', 'testing', 'manufacturing'].includes(currentPhase)) {
      return null;
    }

    // Get simple preference info
    const phaseKey = `${currentPhase}_complete` as keyof PhaseProgressionPreferences;
    const preferenceInfo = getPreferenceInfo(phaseKey, this.phasePreferences);
    
    return {
      preference: preferenceInfo.preference,
      label: preferenceInfo.label,
      color: preferenceInfo.color
    };
  }

  // Phase event triggers using simple preference system
  triggerPhaseCompleteEvent(productId: string, productName: string, phase: string, gameState: TycoonGameState, product?: any): void {
    if (!this.phasePreferences) {
      console.warn('Phase preferences not available for phase complete event');
      return;
    }

    const phaseEvents = this.eventDefinitions.filter(event => 
      event.triggers.some(trigger => trigger.type === 'phase_complete' && trigger.phase === phase)
    );

    phaseEvents.forEach(event => {
      // Check investment count to determine if we should force progression
      const currentInvestmentCount = product?.developmentPhase?.investmentCount || 0;
      const maxInvestmentCount = DEVELOPMENT_CONSTANTS.investmentQualityDiminishingSteps;
      const hasReachedInvestmentLimit = currentInvestmentCount >= maxInvestmentCount;
      
      // Use simple preference system to determine what to do
      const phaseKey = `${phase}_complete` as keyof PhaseProgressionPreferences;
      const userPreference = getPhaseChoice(phaseKey, this.phasePreferences!);
      const shouldPrompt = shouldShowPhasePrompt(phaseKey, this.phasePreferences!);
      
      // Determine actual choice based on investment count and user preference
      let finalChoice = userPreference;
      
      // If investment limit reached, force progression regardless of user preference
      if (hasReachedInvestmentLimit && (userPreference === 'minor' || userPreference === 'major')) {
        finalChoice = 'proceed';
        console.log(`Investment limit (${maxInvestmentCount}) reached for ${productName} in ${phase} phase - forcing progression to next phase`);
      }
      
      // If user preference is "ask" or we need to force progression, handle accordingly
      if (shouldPrompt && !hasReachedInvestmentLimit) {
        // User wants to be prompted and hasn't reached investment limit
        console.log(`Phase ${phase} complete for ${productName}: showing prompt (preference is "ask")`);
        this.createManualPhaseEvent(event, productId, productName, phase, gameState);
      } else if (finalChoice) {
        // Auto-handle with the determined choice
        console.log(`Phase ${phase} complete for ${productName}: auto-applying choice "${finalChoice}" (user preference: "${userPreference}", investments: ${currentInvestmentCount})`);
        
        const result = this.autoHandlePhaseEventSimple(
          event, 
          productId, 
          productName, 
          phase, 
          finalChoice, 
          gameState
        );
        
        if (result.success) {
          console.log(`Auto-handled phase event ${event.id} with choice ${result.choiceId} for product ${productName}`);
          
          // Notify listeners about the auto-handled event for logging purposes
          const resolvedEvent = this.state.eventHistory.find(e => 
            e.event.id === event.id && e.productContext?.productId === productId
          );
          if (resolvedEvent) {
            this.listeners.forEach(listener => listener(resolvedEvent));
          }
        } else {
          console.warn(`Failed to auto-handle phase event: ${result.error}`);
          // Fall back to manual handling if auto-handle fails
          this.createManualPhaseEvent(event, productId, productName, phase, gameState);
        }
      }
    });
  }

  private createManualPhaseEvent(event: DynamicEvent, productId: string, productName: string, phase: string, gameState: TycoonGameState): void {
    const activeEvent = this.activateEvent(event, this.state.lastProcessedMonth + 1, gameState);
    
    // Replace {productName} placeholder in event text
    if (activeEvent.event.title) {
      activeEvent.event.title = activeEvent.event.title.replace('{productName}', productName);
    }
    if (activeEvent.event.description) {
      activeEvent.event.description = activeEvent.event.description.replace('{productName}', productName);
    }
    
    // Store product context for this event
    activeEvent.productContext = { productId, productName, phase };
    
    this.listeners.forEach(listener => listener(activeEvent));
  }


  triggerPhaseStartEvent(productId: string, productName: string, phase: string, gameState: TycoonGameState): void {
    const phaseEvents = this.eventDefinitions.filter(event => 
      event.triggers.some(trigger => trigger.type === 'phase_start' && trigger.phase === phase)
    );

    phaseEvents.forEach(event => {
      const activeEvent = this.activateEvent(event, this.state.lastProcessedMonth + 1, gameState);
      
      // Replace {productName} placeholder in event text
      if (activeEvent.event.title) {
        activeEvent.event.title = activeEvent.event.title.replace('{productName}', productName);
      }
      if (activeEvent.event.description) {
        activeEvent.event.description = activeEvent.event.description.replace('{productName}', productName);
      }

      // Store product context for this event
      activeEvent.productContext = { productId, productName, phase };

      // For systemic events (phase start), immediately resolve them
      if (event.type === 'systemic' && event.automaticOutcome) {
        this.resolveEvent(activeEvent, event.automaticOutcome);
      }
      
      this.listeners.forEach(listener => listener(activeEvent));
    });
  }

  // Utility methods
  getActiveEvents(): ActiveEvent[] {
    return [...this.state.activeEvents];
  }

  getEventHistory(): ActiveEvent[] {
    return [...this.state.eventHistory];
  }

  getPendingReactiveEvents(): ActiveEvent[] {
    return this.state.activeEvents.filter(ae => 
      ae.event.type === 'reactive' && ae.status === 'pending'
    );
  }

  // Restore method for loading saved games
  restorePendingEvent(event: Omit<ActiveEvent, 'triggeredAt' | 'resolvedAt' | 'expiresAt'> & { 
    triggeredAt?: string | Date; 
    resolvedAt?: string | Date;
    expiresAt?: string | Date;
  }): void {
    console.log('EventEngine: Restoring pending event', event.event.title);
    
    // Convert date strings back to Date objects
    const restoredEvent: ActiveEvent = {
      ...event,
      triggeredAt: event.triggeredAt ? new Date(event.triggeredAt) : new Date(),
      resolvedAt: event.resolvedAt ? new Date(event.resolvedAt) : undefined,
      expiresAt: event.expiresAt ? new Date(event.expiresAt) : undefined
    } as ActiveEvent;
    
    // Only restore pending reactive events
    if (event.event.type === 'reactive' && event.status === 'pending') {
      this.state.activeEvents.push(restoredEvent);
      this.state.eventHistory.push(restoredEvent);
      
      // Notify listeners about the restored event
      this.listeners.forEach(listener => listener(restoredEvent));
    }
  }
}

// Singleton instance
export const eventEngine = new EventEngine();
